import api from "./apiClient";

/* ===================================================== */
/* 🔧 ERROR NORMALIZER                                   */
/* ===================================================== */

function normalizeError(err, fallbackMessage) {
  return new Error(
    err?.response?.data?.message ||
      err?.message ||
      fallbackMessage
  );
}

/* ===================================================== */
/* GET ALL TEAMS                                         */
/* GET /teams                                            */
/* ===================================================== */

export const getTeams = async () => {
  try {
    const { data } = await api.get("/teams");
    return Array.isArray(data) ? data : [];
  } catch (err) {
    throw normalizeError(err, "Failed to fetch teams");
  }
};

/* ===================================================== */
/* GET SINGLE TEAM                                       */
/* GET /teams/:id                                        */
/* ===================================================== */

export const getTeam = async (teamId) => {
  if (!teamId) {
    throw new Error("Team ID is required");
  }

  try {
    const { data } = await api.get(`/teams/${teamId}`);
    return data;
  } catch (err) {
    throw normalizeError(err, "Failed to fetch team");
  }
};

/* ===================================================== */
/* CREATE TEAM                                           */
/* POST /teams                                           */
/* ===================================================== */

export const createTeam = async ({ name, type }) => {
  if (!name?.trim()) {
    throw new Error("Team name is required");
  }

  if (!["internal", "contractor"].includes(type)) {
    throw new Error("Team type must be 'internal' or 'contractor'");
  }

  try {
    const { data } = await api.post("/teams", {
      name: name.trim(),
      type,
    });

    return data;
  } catch (err) {
    throw normalizeError(err, "Failed to create team");
  }
};

/* ===================================================== */
/* UPDATE TEAM                                           */
/* PUT /teams/:id                                        */
/* ===================================================== */

export const updateTeam = async (teamId, payload) => {
  if (!teamId) {
    throw new Error("Team ID is required");
  }

  if (!payload || typeof payload !== "object") {
    throw new Error("Update payload is required");
  }

  try {
    const { data } = await api.put(`/teams/${teamId}`, payload);
    return data;
  } catch (err) {
    throw normalizeError(err, "Failed to update team");
  }
};

/* ===================================================== */
/* DELETE TEAM                                           */
/* DELETE /teams/:id                                     */
/* ===================================================== */

export const deleteTeam = async (teamId) => {
  if (!teamId) {
    throw new Error("Team ID is required");
  }

  try {
    const { data } = await api.delete(`/teams/${teamId}`);
    return data;
  } catch (err) {
    throw normalizeError(err, "Failed to delete team");
  }
};

/* ===================================================== */
/* ADD MEMBER TO TEAM                                    */
/* POST /teams/:id/members                               */
/* ===================================================== */

export const addMemberToTeam = async (teamId, userId) => {
  if (!teamId) {
    throw new Error("Team ID is required");
  }

  if (!userId) {
    throw new Error("User ID is required");
  }

  try {
    const { data } = await api.post(`/teams/${teamId}/members`, {
      userId,
    });

    return data;
  } catch (err) {
    throw normalizeError(err, "Failed to add member to team");
  }
};

/* ===================================================== */
/* REMOVE MEMBER FROM TEAM                               */
/* DELETE /teams/:id/members/:userId                     */
/* ===================================================== */

export const removeMemberFromTeam = async (teamId, userId) => {
  if (!teamId) {
    throw new Error("Team ID is required");
  }

  if (!userId) {
    throw new Error("User ID is required");
  }

  try {
    const { data } = await api.delete(
      `/teams/${teamId}/members/${userId}`
    );

    return data;
  } catch (err) {
    throw normalizeError(err, "Failed to remove member");
  }
};

/* ===================================================== */
/* TEAM PERFORMANCE                                      */
/* GET /teams/:id/performance                            */
/* ===================================================== */

export const getTeamPerformance = async (teamId) => {
  if (!teamId) {
    throw new Error("Team ID is required");
  }

  try {
    const { data } = await api.get(
      `/teams/${teamId}/performance`
    );

    return data;
  } catch (err) {
    throw normalizeError(
      err,
      "Failed to fetch team performance"
    );
  }
};

/* ===================================================== */
/* EXPORT                                                */
/* ===================================================== */

const teamService = {
  getTeams,
  getTeam,
  createTeam,
  updateTeam,
  deleteTeam,
  addMemberToTeam,
  removeMemberFromTeam,
  getTeamPerformance,
};

export default teamService;